import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import {
  Plus, Trash2, KeyRound, Copy, Check, Pencil,
  Archive, ArchiveRestore, Upload, Download, AlertTriangle,
  ChevronDown, ChevronRight, MoreVertical, RefreshCw,
} from "lucide-react";
import PlatformUsers from "@/pages/PlatformUsers";
import AccessMatrix from "@/components/AccessMatrix";

export default function Users() {
  const { user: me } = useAuth();
  if (me?.role === "super_admin") return <PlatformUsers />;
  return <TeamUsers me={me} />;
}

function TeamUsers({ me }) {
  const { confirm, notify } = useDialog();
  const [tab, setTab] = useState("active"); // "active" | "archived"
  const [users, setUsers] = useState([]);
  const [danglingCount, setDanglingCount] = useState(0);
  const [activeUsers, setActiveUsers] = useState([]); // for the superior dropdown
  const [sites, setSites] = useState([]);
  const [show, setShow] = useState(false);
  const [archiveModal, setArchiveModal] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = { email: "", name: "", password: "", role: "staff", site_ids: [], company_role: "", employee_id: "", date_joined: "", direct_superior_id: "" };
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const [tempPwd, setTempPwd] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [menuFor, setMenuFor] = useState(null); // { user, x, y } | null

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openMenu = (e, u) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuFor({ user: u, x: rect.right - 200, y: rect.bottom + 6 });
  };

  const isAdmin = me?.role === "owner";
  const isManager = me?.role === "manager";
  const canEditTeam = isAdmin || isManager;
  const showingActive = tab === "active";

  const roleOptions = isAdmin
    ? [{ v: "owner", l: "Admin (Owner)" }, { v: "manager", l: "Manager" }, { v: "supervisor", l: "Supervisor" }, { v: "staff", l: "Staff" }]
    : [{ v: "manager", l: "Manager" }, { v: "supervisor", l: "Supervisor" }, { v: "staff", l: "Staff" }];

  const load = async () => {
    const [list, s, active] = await Promise.all([
      api.get(`/users?archived=${showingActive ? "false" : "true"}`),
      api.get("/sites"),
      api.get("/users?archived=false"),
    ]);
    setUsers(list.data.users || []);
    setDanglingCount(list.data.dangling_superior_count || 0);
    setSites(s.data.sites || []);
    setActiveUsers(active.data.users || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setErr(""); setShow(true); };
  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      email: u.email, name: u.name, password: "", role: u.role,
      site_ids: u.site_ids || [], company_role: u.company_role || "",
      employee_id: u.employee_id || "", date_joined: u.date_joined ? u.date_joined.slice(0, 10) : "",
      direct_superior_id: u.direct_superior_id || "",
    });
    setErr(""); setShow(true);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const body = {
        name: form.name, role: form.role, site_ids: form.site_ids,
        company_role: form.company_role,
        employee_id: form.employee_id,
        date_joined: form.date_joined || null,
        direct_superior_id: form.direct_superior_id || null,
      };
      let inviteMsg = null;
      if (editingId) {
        await api.put(`/users/${editingId}`, body);
      } else {
        const inviteEmail = form.email;
        const { data } = await api.post("/users", { ...form, ...body });
        inviteMsg = data?.email_sent
          ? `Login details and a welcome message were emailed to ${inviteEmail}.`
          : "User created. Email service is off — share the password with them manually.";
      }
      setShow(false); setEditingId(null); setForm(emptyForm);
      load();
      if (inviteMsg) await notify({ title: "Team member added", message: inviteMsg, variant: "ok" });
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
  };

  const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setForm((f) => ({ ...f, password: p }));
  };

  const canDelete = (u) => u.id !== me.id && !(isManager && u.role === "owner") && canEditTeam;
  const canArchive = (u) => u.id !== me.id && !(isManager && u.role === "owner") && canEditTeam && !u.is_archived;
  const canResetPwd = (u) => u.id !== me.id && !(isManager && u.role === "owner") && canEditTeam;
  const canEditUser = (u) => !(isManager && u.role === "owner") && canEditTeam;

  const removeUser = async (u) => {
    if (!await confirm({ title: `Remove ${u.name}?`, message: "Permanent delete — for ISO 22000 retention prefer Archive instead. This action cannot be undone.", danger: true, confirmLabel: "Permanent delete" })) return;
    try { await api.delete(`/users/${u.id}`); load(); } catch (e) { await notify({ title: "Delete failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  const restoreUser = async (u) => {
    if (!await confirm({ title: `Restore ${u.name}?`, message: "They will be active again and can sign in.", confirmLabel: "Restore" })) return;
    try { await api.post(`/users/${u.id}/restore`); load(); } catch (e) { await notify({ title: "Restore failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  const resetPwd = async (u) => {
    if (!await confirm({ title: `Reset password for ${u.name}?`, message: "A one-time password will be generated. Share it securely.", confirmLabel: "Reset password" })) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setTempPwd({ user: u, password: data.temporary_password, emailSent: data.email_sent });
    } catch (e) { await notify({ title: "Reset failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  const archivedActiveUsers = useMemo(() => {
    const archivedIds = new Set(users.filter((u) => u.is_archived).map((u) => u.id));
    return activeUsers.filter((u) => archivedIds.has(u.direct_superior_id));
  }, [activeUsers, users]);

  // Direct-superior options: filter to people who share a site with the
  // member being edited (empty site_ids = chain-wide → always included),
  // then sort by App Role rank so admins scroll a sensible hierarchy.
  const RANK = { owner: 0, manager: 1, supervisor: 2, staff: 3 };
  const superiorOptions = useMemo(() => {
    const formSites = new Set(form.site_ids || []);
    return activeUsers
      .filter((u) => {
        if (u.id === editingId) return false;
        const s = u.site_ids || [];
        if (s.length === 0) return true;
        if (formSites.size === 0) return true;
        return s.some((id) => formSites.has(id));
      })
      .sort((a, b) => {
        const ra = RANK[a.role] ?? 99;
        const rb = RANK[b.role] ?? 99;
        if (ra !== rb) return ra - rb;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [activeUsers, form.site_ids, editingId]);

  return (
    <div className="vb-fade-in" data-testid="users-page">
      <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
        <h1 className="vb-heading text-3xl m-0" style={{ color: "var(--vb-text-2)" }}>Team</h1>
        <div className="flex gap-2 flex-wrap">
          {canEditTeam && (
            <button className="vb-btn ghost" onClick={() => setImportOpen(true)} data-testid="bulk-import-btn"><Upload size={16} /> Bulk import</button>
          )}
          {canEditTeam && showingActive && (
            <button className="vb-btn" onClick={openAdd} data-testid="add-user"><Plus size={16} /> Add team member</button>
          )}
        </div>
      </div>

      {showingActive && <AccessMatrix meRole={me?.role} />}

      {showingActive && danglingCount > 0 && (
        <div className="vb-pill warn mb-3" data-testid="dangling-banner">
          <AlertTriangle size={14} /> {danglingCount} active member{danglingCount === 1 ? "" : "s"} reference an archived superior. Reassign to keep the chain of command intact.
        </div>
      )}

      <div className="vb-tabs mb-4" role="tablist">
        <button role="tab" aria-selected={showingActive} className={`vb-tab ${showingActive ? "active" : ""}`} onClick={() => setTab("active")} data-testid="tab-active">Active</button>
        <button role="tab" aria-selected={!showingActive} className={`vb-tab ${!showingActive ? "active" : ""}`} onClick={() => setTab("archived")} data-testid="tab-archived">Archived</button>
      </div>

      <div className="vb-card overflow-x-auto">
        <table className="vb-table">
          <thead><tr>
            <th style={{ width: 28 }}></th>
            <th>Name</th>
            <th>App role</th>
            <th>Company role</th>
            <th>Sites</th>
            <th style={{ width: 110, textAlign: "right" }}></th>
          </tr></thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 32 }} className="opacity-60">{showingActive ? "No active team members yet." : "Nothing in archive."}</td></tr>}
            {users.map((u) => {
              const sup = activeUsers.find((x) => x.id === u.direct_superior_id) || users.find((x) => x.id === u.direct_superior_id);
              const supArchived = sup?.is_archived;
              const isOpen = expanded.has(u.id);
              const siteNames = (u.site_ids || []).map((id) => sites.find((s) => s.id === id)?.name).filter(Boolean);
              const canEdit = canEditUser(u);
              const showMenu = canResetPwd(u) || canArchive(u) || canDelete(u) || (!showingActive && canEditTeam);
              return (
                <FragmentRow key={u.id}>
                  <tr
                    data-testid={`user-row-${u.id}`}
                    onClick={() => toggleExpanded(u.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td onClick={(e) => { e.stopPropagation(); toggleExpanded(u.id); }} style={{ width: 28 }}>
                      <button
                        type="button"
                        aria-label={isOpen ? "Collapse" : "Expand"}
                        data-testid={`row-toggle-${u.id}`}
                        style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, color: "var(--vb-text-2)" }}
                      >
                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </td>
                    <td>
                      <div onClick={(e) => e.stopPropagation()} className="inline-block">
                        <NavLink to={`/app/users/${u.id}`} className="vb-link inline-flex items-center gap-2.5" data-testid={`profile-link-${u.id}`}>
                          <MiniAvatar user={u} />
                          <strong>{u.name}</strong>
                        </NavLink>
                      </div>
                      {u.id === me.id && <span className="vb-pill ok ml-2 text-[10px]">you</span>}
                      {u.must_reset_password && <span className="vb-pill warn ml-2 text-[10px]">pwd reset pending</span>}
                    </td>
                    <td><span className="vb-pill">{u.role}</span></td>
                    <td>{u.company_role || <span className="opacity-40">—</span>}</td>
                    <td className="text-sm">
                      {siteNames.length === 0 ? <span className="opacity-40">—</span>
                        : siteNames.length <= 2 ? siteNames.join(", ")
                        : <>
                            {siteNames[0]}
                            <span className="opacity-60"> +{siteNames.length - 1} more</span>
                          </>}
                    </td>
                    <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        {canEdit && (
                          <button className="vb-btn ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} title="Edit"><Pencil size={14} /></button>
                        )}
                        {showMenu && (
                          <button className="vb-btn ghost" onClick={(e) => openMenu(e, u)} data-testid={`menu-${u.id}`} title="More actions"><MoreVertical size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`user-row-detail-${u.id}`} style={{ background: "color-mix(in srgb, var(--vb-bg) 60%, transparent)" }}>
                      <td></td>
                      <td colSpan={5} className="text-sm" style={{ paddingTop: 8, paddingBottom: 14 }}>
                        <div className="grid gap-x-6 gap-y-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                          <DetailField label="Email">
                            <a className="vb-link" href={`mailto:${u.email}`}>{u.email}</a>
                          </DetailField>
                          <DetailField label="Employee ID" value={u.employee_id} />
                          <DetailField label="Date joined" value={u.date_joined ? new Date(u.date_joined).toLocaleDateString() : null} />
                          <DetailField label="Direct superior">
                            {sup ? (
                              <span className={supArchived ? "vb-pill warn text-[11px]" : ""}
                                title={supArchived ? "Their direct superior is archived — reassign in edit." : ""}>
                                {sup.name}{supArchived ? " (archived)" : ""}
                              </span>
                            ) : <span className="opacity-40">—</span>}
                          </DetailField>
                          {siteNames.length > 2 && (
                            <DetailField label="All sites">
                              <span>{siteNames.join(", ")}</span>
                            </DetailField>
                          )}
                          {!showingActive && (
                            <>
                              <DetailField label="Archived on" value={u.archived_at ? new Date(u.archived_at).toLocaleDateString() : null} />
                              <DetailField label="Reason" value={u.archived_reason} />
                            </>
                          )}
                        </div>
                        {!showingActive && canEditTeam && (
                          <div className="mt-3">
                            <button className="vb-btn ghost text-sm" onClick={() => restoreUser(u)} data-testid={`restore-${u.id}`}>
                              <ArchiveRestore size={14} /> Restore
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </FragmentRow>
              );
            })}
            {!showingActive && archivedActiveUsers.length > 0 && (
              <tr><td colSpan={6} className="text-xs opacity-70" style={{ background: "var(--vb-bg)" }}>
                Note: <strong>{archivedActiveUsers.length}</strong> active member{archivedActiveUsers.length === 1 ? "" : "s"} report{archivedActiveUsers.length === 1 ? "s" : ""} to someone in this archive. Switch to Active and update their direct superior.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Row actions dropdown — portaled to body so it floats over table scroll. */}
      {menuFor && createPortal(
        <RowMenu
          target={menuFor}
          onClose={() => setMenuFor(null)}
          showingActive={showingActive}
          canEditTeam={canEditTeam}
          canResetPwd={canResetPwd}
          canArchive={canArchive}
          canDelete={canDelete}
          onResetPwd={resetPwd}
          onArchive={(u) => setArchiveModal(u)}
          onDelete={removeUser}
          onRestore={restoreUser}
        />,
        document.body
      )}

      {tempPwd && createPortal(
        <div className="vb-modal-bg" onClick={() => { setTempPwd(null); setCopied(false); }}>
          <div className="vb-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Temporary password generated</h2>
            <p className="text-sm opacity-80 mb-3">
              {tempPwd.emailSent
                ? <>The new password was <strong>emailed to {tempPwd.user.email}</strong> with sign-in instructions. You can also copy it below. They'll be required to change it on next login.</>
                : <>Share this securely with <strong>{tempPwd.user.name}</strong>. They'll be required to change it on next login.</>}
            </p>
            <div className="flex gap-2 mb-4">
              <input className="vb-input font-mono" readOnly value={tempPwd.password} />
              <button className="vb-btn" onClick={() => { navigator.clipboard.writeText(tempPwd.password); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <button className="vb-btn ghost w-full" onClick={() => { setTempPwd(null); setCopied(false); }}>Close</button>
          </div>
        </div>,
        document.body
      )}

      {show && createPortal(
        <div className="vb-modal-bg" onClick={() => { setShow(false); setEditingId(null); }}>
          <div className="vb-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingId ? "Edit team member" : "Add team member"}</h2>
            {err && <div className="vb-pill err mb-3">{err}</div>}
            <form onSubmit={submit} className="grid gap-3">
              <input className="vb-input" placeholder="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
              <input className="vb-input" placeholder="Email / Username" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editingId} data-testid="user-email" />
              {!editingId && (
                <div className="flex gap-2">
                  <input className="vb-input flex-1" placeholder="Initial password (≥6 chars)" required minLength={6} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
                  <button type="button" className="vb-btn ghost shrink-0" onClick={genPassword} data-testid="user-password-generate" title="Auto-generate a strong password">
                    <RefreshCw size={14} /> Generate
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <select className="vb-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role" disabled={editingId === me.id && me.role === "owner"} title={editingId === me.id ? "You cannot change your own admin role" : ""}>
                  {roleOptions.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
                <input className="vb-input" placeholder="Employee ID (optional)" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="user-employee-id" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs opacity-70 block mb-1">Date joined</label>
                  <input className="vb-input" type="date" value={form.date_joined} onChange={(e) => setForm({ ...form, date_joined: e.target.value })} data-testid="user-date-joined" />
                </div>
                <div>
                  <label className="text-xs opacity-70 block mb-1">Direct superior</label>
                  <select className="vb-input" value={form.direct_superior_id} onChange={(e) => setForm({ ...form, direct_superior_id: e.target.value })} data-testid="user-superior">
                    <option value="">— None —</option>
                    {superiorOptions.map((u) => (
                      <option key={u.id} value={u.id}>{u.name} · {u.role}{u.company_role ? ` · ${u.company_role}` : ""}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase font-semibold opacity-70">Company role (optional)</label>
                <input className="vb-input mt-1" placeholder="e.g. Head Chef, F&B Manager" value={form.company_role} onChange={(e) => setForm({ ...form, company_role: e.target.value })} data-testid="user-company-role" maxLength={80} />
                <p className="text-[11px] opacity-60 mt-1">Their job title in your organisation. Shown in reports and audit trail. Does not affect access.</p>
              </div>
              <div>
                <label className="text-xs uppercase font-semibold opacity-70 block mb-2">Sites</label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                  {sites.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.site_ids.includes(s.id)} onChange={(e) => {
                        const next = e.target.checked ? [...form.site_ids, s.id] : form.site_ids.filter((x) => x !== s.id);
                        setForm({ ...form, site_ids: next });
                      }} />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" className="vb-btn ghost" onClick={() => { setShow(false); setEditingId(null); }}>Cancel</button>
                <button type="submit" className="vb-btn" data-testid="user-submit">Save</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {archiveModal && <ArchiveModal user={archiveModal} onClose={() => setArchiveModal(null)} onDone={() => { setArchiveModal(null); load(); }} />}
      {importOpen && <BulkImportWizard onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}
    </div>
  );
}

// -------------------- Archive modal --------------------
function ArchiveModal({ user, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [lastDay, setLastDay] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [danglingCount, setDanglingCount] = useState(null);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/users/${user.id}/archive`, { reason, last_day: lastDay || null });
      if (data.dangling_reports > 0) setDanglingCount(data.dangling_reports);
      else onDone();
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="vb-modal-bg" onClick={onClose}>
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} data-testid="archive-modal">
        {danglingCount === null ? (
          <>
            <h2>Archive {user.name}?</h2>
            <p className="text-sm opacity-80 mb-4">Their record (training, hygiene checks, audit history) will be retained for ISO 22000 compliance — they just won't be able to sign in or be assigned to anything new.</p>
            {err && <div className="vb-pill err mb-3">{err}</div>}
            <label className="text-xs uppercase font-semibold opacity-70 block mb-1">Last day (optional)</label>
            <input className="vb-input mb-3" type="date" value={lastDay} onChange={(e) => setLastDay(e.target.value)} data-testid="archive-last-day" />
            <label className="text-xs uppercase font-semibold opacity-70 block mb-1">Reason (optional, recorded in audit log)</label>
            <textarea className="vb-input mb-4" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Resigned · End of contract" data-testid="archive-reason" />
            <div className="flex gap-2 justify-end">
              <button className="vb-btn ghost" onClick={onClose}>Cancel</button>
              <button className="vb-btn" onClick={submit} disabled={busy} data-testid="archive-submit">{busy ? "Archiving…" : "Archive"}</button>
            </div>
          </>
        ) : (
          <>
            <div className="vb-pill warn mb-3"><AlertTriangle size={14} /> {danglingCount} active member{danglingCount === 1 ? "" : "s"} report{danglingCount === 1 ? "ed" : "ed"} to {user.name}.</div>
            <p className="text-sm opacity-80 mb-4">Edit each affected member to assign a new direct superior — the Team list will flag them with a warning until you do.</p>
            <button className="vb-btn w-full" onClick={onDone}>Got it</button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// -------------------- Bulk import wizard --------------------
function BulkImportWizard({ onClose, onDone }) {
  const [step, setStep] = useState("upload"); // upload | preview | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState([]);
  const [updated, setUpdated] = useState([]);
  const [archived, setArchived] = useState([]);
  const [archiveRemoved, setArchiveRemoved] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/users/bulk-import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "vanguard-team-import-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Couldn't download template"); }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/users/bulk-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setStep("preview");
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setErr("");
    try {
      const rows = [...(preview.valid || []), ...(preview.updates || [])].map((r) => ({
        email: r.email, name: r.name, role: r.role, employee_id: r.employee_id,
        date_joined: r.date_joined || null, site_names: r.site_names || [],
        company_role: r.company_role || "",
      }));
      const body = {
        rows,
        remove_emails: archiveRemoved ? (preview.removed || []).map((r) => r.email) : [],
      };
      const { data } = await api.post("/users/bulk-import/commit", body);
      setCreated(data.created || []); setUpdated(data.updated || []);
      setArchived(data.archived || []); setStep("done");
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Commit failed"); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="vb-modal-bg" onClick={onClose}>
      <div className="vb-modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()} data-testid="import-modal">
        <h2>Bulk import team members</h2>

        {step === "upload" && (
          <>
            <p className="text-sm opacity-80 mb-3">The template comes pre-filled with your current team. <strong>Add</strong> new members in the empty rows, or <strong>edit</strong> an existing member's details in place. On upload we'll show exactly what's new, what changed, and what's unchanged — nothing is saved until you confirm.</p>
            <button className="vb-btn ghost mb-4" onClick={downloadTemplate} data-testid="import-template-btn"><Download size={16} /> Download Excel template</button>
            <div className="vb-card p-4 mb-3" style={{ background: "var(--vb-bg)" }}>
              <input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file" />
              <p className="text-[11px] opacity-60 mt-2">Columns: Name, Email, App role, Employee ID, Date joined, Company role, then one or more Site columns (pick from the dropdowns).</p>
            </div>
            {err && <div className="vb-pill err mb-3">{err}</div>}
            <div className="flex gap-2 justify-end">
              <button className="vb-btn ghost" onClick={onClose}>Cancel</button>
              <button className="vb-btn" onClick={upload} disabled={!file || busy} data-testid="import-preview-btn">{busy ? "Reading…" : "Preview rows"}</button>
            </div>
          </>
        )}

        {step === "preview" && preview && (
          <>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="vb-card p-3 text-center"><div className="text-2xl font-bold">{preview.summary.total}</div><div className="text-xs opacity-60">Rows in file</div></div>
              <div className="vb-card p-3 text-center" style={{ borderLeft: "3px solid var(--vb-success)" }}><div className="text-2xl font-bold" data-testid="import-new-count">{preview.summary.valid}</div><div className="text-xs opacity-60">New — add</div></div>
              <div className="vb-card p-3 text-center" style={{ borderLeft: "3px solid var(--vb-warning)" }}><div className="text-2xl font-bold" data-testid="import-updates-count">{preview.summary.updates ?? 0}</div><div className="text-xs opacity-60">Changed — update</div></div>
              <div className="vb-card p-3 text-center" style={{ borderLeft: "3px solid var(--vb-text-2)" }}><div className="text-2xl font-bold" data-testid="import-existing-count">{preview.summary.existing ?? 0}</div><div className="text-xs opacity-60">Unchanged</div></div>
              <div className="vb-card p-3 text-center" style={{ borderLeft: "3px solid var(--vb-error)" }}><div className="text-2xl font-bold" data-testid="import-removed-count">{preview.summary.removed ?? 0}</div><div className="text-xs opacity-60">Missing (removable)</div></div>
              <div className="vb-card p-3 text-center" style={{ borderLeft: "3px solid var(--vb-error)" }}><div className="text-2xl font-bold">{preview.summary.errors}</div><div className="text-xs opacity-60">Issues</div></div>
            </div>

            {(preview.updates?.length > 0) && (
              <div className="vb-card p-3 mb-3" data-testid="import-updates" style={{ background: "rgba(202, 138, 4, 0.08)" }}>
                <div className="text-sm font-semibold mb-2 flex items-center gap-2"><AlertTriangle size={14} /> {preview.updates.length} existing member{preview.updates.length === 1 ? "" : "s"} changed — confirm to update</div>
                <ul className="text-xs space-y-2 max-h-56 overflow-y-auto">
                  {preview.updates.map((u) => (
                    <li key={u.email} data-testid={`import-update-${u.email}`}>
                      <strong>{u.name}</strong> <span className="opacity-60">({u.email})</span>
                      <ul className="mt-1 ml-3 space-y-0.5">
                        {u.changes.map((c, i) => (
                          <li key={i}>{c.field}: <span className="opacity-60 line-through">{String(c.from)}</span> → <span style={{ color: "var(--vb-text-2)", fontWeight: 600 }}>{String(c.to)}</span></li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(preview.removed?.length > 0) && (
              <div className="vb-card p-3 mb-3" data-testid="import-removed" style={{ background: "rgba(220, 38, 38, 0.08)", borderLeft: "3px solid var(--vb-error)" }}>
                <div className="text-sm font-semibold mb-1 flex items-center gap-2" style={{ color: "var(--vb-error)" }}><AlertTriangle size={14} /> {preview.removed.length} team member{preview.removed.length === 1 ? " is" : "s are"} missing from the file</div>
                <div className="text-xs opacity-75 mb-2">{preview.removed.map((r) => `${r.name} (${r.email})`).join(", ")}</div>
                <label className="flex items-start gap-2 text-xs cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={archiveRemoved} onChange={(e) => setArchiveRemoved(e.target.checked)} data-testid="import-archive-removed" />
                  <span>Archive these {preview.removed.length} member{preview.removed.length === 1 ? "" : "s"} (they lose access but their historical records are kept for audit). Leave unchecked to keep them.</span>
                </label>
              </div>
            )}

            {(preview.existing?.length > 0) && (
              <div className="vb-card p-3 mb-3" data-testid="import-existing" style={{ background: "var(--vb-bg-2)" }}>
                <div className="text-sm font-semibold mb-1 flex items-center gap-2"><Check size={14} /> {preview.existing.length} unchanged — skipped</div>
                <div className="text-xs opacity-70 max-h-24 overflow-y-auto">{preview.existing.map((e) => e.email).join(", ")}</div>
              </div>
            )}

            {preview.errors.length > 0 && (
              <div className="vb-card p-3 mb-3" style={{ background: "rgba(196, 84, 84, 0.08)" }} data-testid="import-errors">
                <div className="text-sm font-semibold mb-2 flex items-center gap-2"><AlertTriangle size={14} /> Issues — fix the file and re-upload</div>
                <ul className="text-xs space-y-2 max-h-48 overflow-y-auto">
                  {preview.errors.map((er) => (
                    <li key={er.row_number}><strong>Row {er.row_number}</strong> ({er.email || er.name || "—"}): {er.issues.join("; ")}</li>
                  ))}
                </ul>
              </div>
            )}

            {preview.valid.length > 0 && (
              <div className="vb-card overflow-x-auto mb-3">
                <table className="vb-table text-xs">
                  <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Sites</th></tr></thead>
                  <tbody>{preview.valid.slice(0, 10).map((r) => (<tr key={r.email}><td>{r.name}</td><td>{r.email}</td><td>{r.role}</td><td>{r.site_names.join("; ") || "—"}</td></tr>))}</tbody>
                </table>
                {preview.valid.length > 10 && <div className="text-xs opacity-60 p-2">+ {preview.valid.length - 10} more…</div>}
              </div>
            )}

            {err && <div className="vb-pill err mb-3">{err}</div>}
            {(() => {
              const n = (preview.summary.valid || 0) + (preview.summary.updates || 0) + (archiveRemoved ? (preview.summary.removed || 0) : 0);
              const hasErrors = (preview.summary.errors || 0) > 0;
              return (
                <>
                  {hasErrors && (
                    <div className="vb-pill err mb-3" data-testid="import-blocked-note">
                      <AlertTriangle size={14} /> Fix the {preview.summary.errors} row{preview.summary.errors === 1 ? "" : "s"} with issues (or remove them from the file) before submitting.
                    </div>
                  )}
                  <div className="flex gap-2 justify-end">
                    <button className="vb-btn ghost" onClick={() => { setStep("upload"); setPreview(null); setFile(null); setArchiveRemoved(false); }}>Re-upload</button>
                    <button className="vb-btn" onClick={commit} disabled={busy || hasErrors || n === 0} data-testid="import-commit-btn">{busy ? "Applying…" : `Apply ${n} change${n === 1 ? "" : "s"}`}</button>
                  </div>
                </>
              );
            })()}
          </>
        )}

        {step === "done" && (
          <>
            <div className="vb-pill ok mb-3" data-testid="import-done"><Check size={14} /> Added {created.length} · Updated {updated.length} · Archived {archived.length}</div>
            {created.length > 0 && (
              <>
                <p className="text-sm opacity-80 mb-3">New members received a one-time password. Share these securely — they'll be forced to change at first login.</p>
                <div className="vb-card overflow-x-auto mb-3">
                  <table className="vb-table text-xs">
                    <thead><tr><th>Name</th><th>Email</th><th>Temporary password</th></tr></thead>
                    <tbody>{created.map((r) => (<tr key={r.email}><td>{r.name}</td><td>{r.email}</td><td className="font-mono">{r.temp_password}</td></tr>))}</tbody>
                  </table>
                </div>
              </>
            )}
            {created.length === 0 && (updated.length > 0 || archived.length > 0) && (
              <p className="text-sm opacity-80 mb-3">
                {updated.length > 0 && `${updated.length} member${updated.length === 1 ? "'s details were" : "s' details were"} updated. `}
                {archived.length > 0 && `${archived.length} member${archived.length === 1 ? " was" : "s were"} archived.`}
              </p>
            )}
            <button className="vb-btn w-full" onClick={onDone}>Done</button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// Tiny avatar (28px) for directory rows — initials fallback with deterministic warm color.
const MINI_PALETTE = ["#7B4A2D", "#A0623A", "#5C3A22", "#8B5A3C", "#6E4327", "#A57044", "#4F3220"];
const miniColorFor = (key) => {
  if (!key) return MINI_PALETTE[0];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return MINI_PALETTE[h % MINI_PALETTE.length];
};
const miniInitials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

function MiniAvatar({ user }) {
  if (user?.avatar_url) {
    return <img src={user.avatar_url} alt="" className="vb-mini-avatar" data-testid={`mini-avatar-${user.id}`} />;
  }
  return (
    <span
      className="vb-mini-avatar vb-mini-avatar-fallback"
      style={{ background: miniColorFor(user?.id || user?.name) }}
      data-testid={`mini-avatar-${user?.id || ""}`}
    >
      {miniInitials(user?.name)}
    </span>
  );
}


// React.Fragment wrapper that React needs a key on; using a plain Fragment with
// a key inline keeps the inner `<tr>` pair valid markup inside `<tbody>`.
function FragmentRow({ children }) {
  return <>{children}</>;
}

function DetailField({ label, value, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider opacity-55 mb-0.5">{label}</div>
      <div className="text-sm">
        {children !== undefined ? children
          : value ? value
          : <span className="opacity-40">—</span>}
      </div>
    </div>
  );
}

// Floating per-row action menu (portaled to document.body to escape table
// overflow). Closes on outside-click and Escape.
function RowMenu({ target, onClose, showingActive, canEditTeam, canResetPwd, canArchive, canDelete, onResetPwd, onArchive, onDelete, onRestore }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const u = target.user;
  const run = (fn) => () => { onClose(); fn(u); };

  // Compose action list based on permissions.
  const items = [];
  if (showingActive) {
    if (canResetPwd(u)) items.push({ key: "reset", icon: <KeyRound size={14} />, label: "Reset password", onClick: run(onResetPwd), tid: `reset-pwd-${u.id}` });
    if (canArchive(u)) items.push({ key: "archive", icon: <Archive size={14} />, label: "Archive", onClick: run(onArchive), tid: `archive-${u.id}` });
    if (canDelete(u)) items.push({ key: "delete", icon: <Trash2 size={14} />, label: "Delete (permanent)", danger: true, onClick: run(onDelete), tid: `del-user-${u.id}` });
  } else {
    if (canEditTeam) items.push({ key: "restore", icon: <ArchiveRestore size={14} />, label: "Restore", onClick: run(onRestore), tid: `restore-${u.id}` });
  }

  return (
    <div
      ref={ref}
      role="menu"
      data-testid={`row-menu-${u.id}`}
      style={{
        position: "fixed",
        top: target.y,
        left: Math.max(8, target.x),
        zIndex: 60,
        minWidth: 200,
        background: "var(--vb-card)",
        border: "1px solid rgba(0,0,0,0.10)",
        borderRadius: 12,
        boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
        overflow: "hidden",
      }}
    >
      <ul style={{ margin: 0, padding: 4, listStyle: "none" }}>
        {items.length === 0 && (
          <li style={{ padding: "10px 14px", fontSize: 13, opacity: 0.6 }}>No actions available.</li>
        )}
        {items.map((it) => (
          <li key={it.key}>
            <button
              type="button"
              onClick={it.onClick}
              data-testid={it.tid}
              role="menuitem"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                fontSize: 13.5,
                color: it.danger ? "var(--vb-error)" : "var(--vb-text-2)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "color-mix(in srgb, var(--vb-accent) 8%, transparent)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              {it.icon} {it.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
